import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createHolding,
  getBrokers,
  getHolding,
  getStockTickers,
  updateHolding,
} from "../../services/portfolioService";

// Market configuration with auto-suffixes
const MARKETS = [
  {
    id: "US",
    name: "United States (NASDAQ/NYSE)",
    suffix: "",
    currency: "USD",
    example: "AAPL",
    exchange: "NASDAQ",
  },
  {
    id: "BVB",
    name: "Romania (BVB)",
    suffix: ".RO",
    currency: "RON",
    example: "SNN",
    exchange: "BVB",
  },
  {
    id: "XETRA",
    name: "Germany (XETRA)",
    suffix: ".DE",
    currency: "EUR",
    example: "BMW",
    exchange: "XETRA",
  },
  {
    id: "LSE",
    name: "United Kingdom (LSE)",
    suffix: ".L",
    currency: "GBP",
    example: "BP",
    exchange: "LSE",
  },
  {
    id: "EURONEXT_PARIS",
    name: "France (Euronext Paris)",
    suffix: ".PA",
    currency: "EUR",
    example: "MC",
    exchange: "EURONEXT",
  },
  {
    id: "EURONEXT_AMSTERDAM",
    name: "Netherlands (Euronext Amsterdam)",
    suffix: ".AS",
    currency: "EUR",
    example: "ASML",
    exchange: "EURONEXT",
  },
  {
    id: "SIX",
    name: "Switzerland (SIX)",
    suffix: ".SW",
    currency: "CHF",
    example: "NESN",
    exchange: "SIX",
  },
  {
    id: "TSE",
    name: "Japan (TSE)",
    suffix: ".T",
    currency: "JPY",
    example: "7203",
    exchange: "TSE",
  },
  {
    id: "HKEX",
    name: "Hong Kong (HKEX)",
    suffix: ".HK",
    currency: "HKD",
    example: "0700",
    exchange: "HKEX",
  },
];

const AddHoldingPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [brokers, setBrokers] = useState([]);
  const [stockTickers, setStockTickers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState("US");
  const [tickerPreview, setTickerPreview] = useState("");

  const [formData, setFormData] = useState({
    broker_id: "",
    ticker_symbol: "",
    company_name: "",
    exchange: "NASDAQ",
    transaction_type: "BUY",
    quantity: "",
    purchase_price: "",
    purchase_date: new Date().toISOString().split("T")[0],
    currency: "USD",
    fees: "0",
    notes: "",
  });

  // Update ticker preview when ticker or market changes
  useEffect(() => {
    if (formData.ticker_symbol) {
      const market = MARKETS.find((m) => m.id === selectedMarket);
      const baseTicker = formData.ticker_symbol.toUpperCase();
      setTickerPreview(baseTicker + (market?.suffix || ""));
    } else {
      setTickerPreview("");
    }
  }, [formData.ticker_symbol, selectedMarket]);

  useEffect(() => {
    fetchBrokers();
    fetchStockTickers();
    if (isEdit) {
      fetchHolding();
    }
  }, [id]);

  const fetchBrokers = async () => {
    try {
      const data = await getBrokers();
      setBrokers(data);
    } catch (error) {
      console.error("Error fetching brokers:", error);
      setError("Failed to load brokers");
    }
  };

  const fetchStockTickers = async () => {
    try {
      const tickers = await getStockTickers();
      setStockTickers(tickers);
    } catch (error) {
      console.error("Error fetching stock tickers:", error);
      // Non-critical error, don't show to user
    }
  };

  const fetchHolding = async () => {
    try {
      setLoading(true);
      const data = await getHolding(id);

      // Convert date to YYYY-MM-DD format for input field
      // Handle timezone: ISO string is UTC, we want local date
      let formattedDate = "";
      if (data.purchase_date) {
        const date = new Date(data.purchase_date);
        // Get local date components
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        formattedDate = `${year}-${month}-${day}`;
      }

      // Detect market from ticker suffix or exchange
      let detectedMarket = "US";
      if (data.ticker_symbol) {
        // Check for suffix
        for (const market of MARKETS) {
          if (market.suffix && data.ticker_symbol.endsWith(market.suffix)) {
            detectedMarket = market.id;
            break;
          }
        }
        // If no suffix match, try exchange
        if (detectedMarket === "US" && data.exchange) {
          const marketByExchange = MARKETS.find(
            (m) => m.exchange === data.exchange
          );
          if (marketByExchange) {
            detectedMarket = marketByExchange.id;
          }
        }
      }

      // Remove suffix from ticker for display
      let displayTicker = data.ticker_symbol;
      const market = MARKETS.find((m) => m.id === detectedMarket);
      if (market && market.suffix && displayTicker.endsWith(market.suffix)) {
        displayTicker = displayTicker.slice(0, -market.suffix.length);
      }

      setFormData({
        broker_id: data.broker_id,
        ticker_symbol: displayTicker,
        company_name: data.company_name || "",
        exchange: data.exchange || "",
        transaction_type: data.transaction_type,
        quantity: data.quantity,
        purchase_price: data.purchase_price,
        purchase_date: formattedDate,
        currency: data.currency,
        fees: data.fees || "0",
        notes: data.notes || "",
      });

      // Set market dropdown
      setSelectedMarket(detectedMarket);
    } catch (error) {
      console.error("Error fetching holding:", error);
      setError("Failed to load holding");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.broker_id) {
      setError("Please select a broker");
      return;
    }
    if (!formData.ticker_symbol) {
      setError("Ticker symbol is required");
      return;
    }
    if (
      formData.transaction_type !== "DIVIDEND" &&
      (!formData.quantity || parseFloat(formData.quantity) <= 0)
    ) {
      setError("Quantity must be greater than 0");
      return;
    }
    // Price validation - not required for DIVIDEND_REINVEST (free shares)
    if (
      formData.transaction_type !== "DIVIDEND_REINVEST" &&
      (!formData.purchase_price || parseFloat(formData.purchase_price) <= 0)
    ) {
      setError(
        formData.transaction_type === "DIVIDEND"
          ? "Dividend amount must be greater than 0"
          : "Purchase price must be greater than 0"
      );
      return;
    }
    if (!formData.purchase_date) {
      setError("Date is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const market = MARKETS.find((m) => m.id === selectedMarket);

      // Check if ticker already has the suffix (important for edit mode!)
      let fullTicker = formData.ticker_symbol.toUpperCase();
      if (market?.suffix && !fullTicker.endsWith(market.suffix)) {
        fullTicker = fullTicker + market.suffix;
      }

      const payload = {
        ...formData,
        ticker_symbol: fullTicker,
        exchange: market?.exchange || formData.exchange,
        quantity: formData.quantity ? parseFloat(formData.quantity) : 0,
        purchase_price: parseFloat(formData.purchase_price),
        fees: parseFloat(formData.fees) || 0,
      };

      if (isEdit) {
        await updateHolding(id, payload);
      } else {
        await createHolding(payload);
      }

      navigate("/stock-transactions");
    } catch (error) {
      console.error("Error saving holding:", error);
      setError(error.response?.data?.message || "Failed to save transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTickerSelect = (ticker) => {
    if (!ticker) return;

    console.log("Autocomplete selected:", ticker);

    // Extract base ticker (remove market suffix)
    let baseTicker = ticker.ticker_symbol;
    let market = null;

    // Try to find market from suffix
    for (const m of MARKETS) {
      if (m.suffix && ticker.ticker_symbol.endsWith(m.suffix)) {
        market = m;
        baseTicker = ticker.ticker_symbol.slice(0, -m.suffix.length);
        break;
      }
    }

    // If no suffix match, try to match by exchange
    if (!market && ticker.exchange) {
      market = MARKETS.find((m) => m.exchange === ticker.exchange);
    }

    // Default to US if still no match
    if (!market) {
      market = MARKETS[0];
    }

    console.log("Detected market:", market.id, market.name);

    // Update form data
    setFormData((prev) => ({
      ...prev,
      ticker_symbol: baseTicker,
      company_name: ticker.company_name || "",
      currency: ticker.currency || market.currency,
      exchange: ticker.exchange || market.exchange,
    }));

    // Update market dropdown
    setSelectedMarket(market.id);
    console.log("Set selectedMarket to:", market.id);
  };

  const isQuantityRequired = formData.transaction_type !== "DIVIDEND";

  if (loading) {
    return (
      <Container
        maxWidth="md"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/stock-transactions")}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5" fontWeight={600}>
            {isEdit ? "Edit Transaction" : "Add New Transaction"}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {brokers.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You need to add a broker first before adding holdings.{" "}
            <Button size="small" onClick={() => navigate("/brokers")}>
              Go to Brokers
            </Button>
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              select
              label="Broker *"
              name="broker_id"
              value={formData.broker_id}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
              disabled={submitting}
            >
              {brokers.map((broker) => (
                <MenuItem key={broker.id} value={broker.id}>
                  {broker.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Market Selection */}
            <TextField
              select
              label="Market / Exchange *"
              value={selectedMarket}
              onChange={(e) => {
                setSelectedMarket(e.target.value);
                const market = MARKETS.find((m) => m.id === e.target.value);
                if (market) {
                  setFormData((prev) => ({
                    ...prev,
                    currency: market.currency,
                    exchange: market.exchange,
                  }));
                }
              }}
              fullWidth
              margin="normal"
              disabled={submitting}
              helperText="Select the stock exchange where you're trading"
            >
              {MARKETS.map((market) => (
                <MenuItem key={market.id} value={market.id}>
                  {market.name}
                </MenuItem>
              ))}
            </TextField>

            <Box display="flex" gap={2}>
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  freeSolo
                  options={stockTickers}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : `${option.ticker_symbol}${
                          option.company_name ? " - " + option.company_name : ""
                        }`
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ticker Symbol *"
                      required
                      margin="normal"
                      placeholder={
                        MARKETS.find((m) => m.id === selectedMarket)?.example ||
                        "e.g., AAPL"
                      }
                      helperText={
                        tickerPreview
                          ? `Will be saved as: ${tickerPreview}`
                          : "Start typing for suggestions"
                      }
                      inputProps={{
                        ...params.inputProps,
                        style: { textTransform: "uppercase" },
                      }}
                      fullWidth
                    />
                  )}
                  onInputChange={(e, value) => {
                    if (e) {
                      setFormData((prev) => ({
                        ...prev,
                        ticker_symbol: value.toUpperCase(),
                      }));
                    }
                  }}
                  onChange={(e, value) => {
                    if (value && typeof value === "object") {
                      handleTickerSelect(value);
                    }
                  }}
                  inputValue={formData.ticker_symbol}
                  filterOptions={(options, { inputValue }) => {
                    return options
                      .filter(
                        (option) =>
                          option.ticker_symbol
                            .toLowerCase()
                            .includes(inputValue.toLowerCase()) ||
                          (option.company_name &&
                            option.company_name
                              .toLowerCase()
                              .includes(inputValue.toLowerCase()))
                      )
                      .slice(0, 10);
                  }}
                  disabled={submitting}
                />
              </Box>

              <TextField
                label="Company Name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                margin="normal"
                disabled={submitting}
                placeholder="e.g., Apple Inc."
                sx={{ flex: 1 }}
              />
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                select
                label="Transaction Type"
                name="transaction_type"
                value={formData.transaction_type}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={submitting}
              >
                <MenuItem value="BUY">🟢 BUY</MenuItem>
                <MenuItem value="SELL">🔴 SELL</MenuItem>
                <MenuItem value="DIVIDEND">💵 DIVIDEND (Cash)</MenuItem>
                <MenuItem value="DIVIDEND_REINVEST">
                  💰 DIVIDEND (Reinvested)
                </MenuItem>
              </TextField>
            </Box>

            {/* Helper text for dividend reinvestment */}
            {formData.transaction_type === "DIVIDEND_REINVEST" && (
              <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body2">
                  💡 <strong>Dividend Reinvestment:</strong>
                  <br />
                  You received <strong>free shares</strong> as dividends. No
                  price entry needed!
                  <br />
                  Just enter the quantity of shares you received.
                  <br />
                  <br />
                  These shares won't affect your average cost, but will increase
                  your total shares and portfolio value.
                </Typography>
              </Alert>
            )}

            <Box display="flex" gap={2}>
              <TextField
                label={
                  isQuantityRequired
                    ? "Quantity *"
                    : "Quantity (optional for dividends)"
                }
                name="quantity"
                type="number"
                value={formData.quantity}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required={isQuantityRequired}
                disabled={submitting}
                inputProps={{ step: "0.000001", min: "0" }}
                placeholder={
                  isQuantityRequired
                    ? "e.g., 10.5"
                    : "Leave empty for cash dividend"
                }
                helperText={
                  !isQuantityRequired
                    ? "For cash dividends, you can leave quantity empty"
                    : ""
                }
              />

              <TextField
                label={
                  formData.transaction_type === "DIVIDEND"
                    ? "Amount per Share *"
                    : "Price per Share *"
                }
                name="purchase_price"
                type="number"
                value={formData.purchase_price}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required={formData.transaction_type !== "DIVIDEND_REINVEST"}
                disabled={
                  submitting ||
                  formData.transaction_type === "DIVIDEND_REINVEST"
                }
                inputProps={{ step: "0.01", min: "0" }}
                placeholder={
                  formData.transaction_type === "DIVIDEND_REINVEST"
                    ? "N/A - Free shares"
                    : "e.g., 150.00"
                }
                helperText={
                  formData.transaction_type === "DIVIDEND_REINVEST"
                    ? "Price not needed for free dividend shares"
                    : formData.transaction_type === "DIVIDEND"
                    ? "Dividend amount per share"
                    : ""
                }
              />
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                label="Purchase Date *"
                name="purchase_date"
                type="date"
                value={formData.purchase_date}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                disabled={submitting}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                select
                label="Currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={submitting}
              >
                <MenuItem value="USD">USD - US Dollar</MenuItem>
                <MenuItem value="EUR">EUR - Euro</MenuItem>
                <MenuItem value="RON">RON - Romanian Leu</MenuItem>
              </TextField>
            </Box>

            <TextField
              label="Fees"
              name="fees"
              type="number"
              value={formData.fees}
              onChange={handleChange}
              fullWidth
              margin="normal"
              disabled={submitting}
              inputProps={{ step: "0.01", min: "0" }}
              placeholder="Transaction fees (optional)"
            />

            <TextField
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              disabled={submitting}
              placeholder="Any additional notes about this holding"
            />

            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <Button
                variant="outlined"
                onClick={() => navigate("/holdings")}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  submitting ? <CircularProgress size={20} /> : <SaveIcon />
                }
                disabled={submitting}
              >
                {submitting
                  ? "Saving..."
                  : isEdit
                  ? "Save Changes"
                  : "Add Holding"}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AddHoldingPage;
